import { Injectable } from '@angular/core';
import { ElementCoreService } from './element-core.service';
import { ApiService } from '../../api/api.service';
import { SubmoduleService } from '../submodule/submodule.service';
import { ProjectElement, ElementLog, ElementDragDrop, ElementLocation } from './project-element.interface';
import { BasicAreaService } from '../basic-area/basic-area.service';
import { ProjectService } from '../project/project.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ModalService } from '../../services/modal/modal.service';
import { Observable } from 'rxjs';
import { User } from '../user/user.interface';
import { ConstantsStatusType } from '../../utilities/constants/constants.interface';
import { ConstantsService } from '../../utilities/constants/constants.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ElementLogService } from '../../utilities/stores/element-log/element-log.service';
import { ElementAssignToOptions } from '../../services/modal/modals/assign-to/assign-to.interface';
import { UserService } from '../user/user.service';
import * as moment from 'moment';
import { RouterService } from '../../utilities/router/router.service';
import { ElementComment, ElementChange, ElementCommentSummary, ElementLogs } from '../log/logs.interface';
import { LogService } from '../log/log.service';
import { ModuleService } from '../module/module.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Element service.
 *
 * The public interface for the ElementCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class ElementService extends ElementCoreService {

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    private constantsService: ConstantsService,
    private elementLogService: ElementLogService,
    // private logService: LogService,
    private modalService: ModalService,
    protected projectService: ProjectService,
    protected routerService: RouterService,
    protected submoduleService: SubmoduleService,
    protected toastService: ToastService,
    protected translateService: TranslateService,
    private userService: UserService
  ) {
    super(apiService, apiLegacyService, projectService, routerService, submoduleService, toastService, translateService);
  }

  /**
   * Insert a new comment.
   *
   * @param element The element to add the comment for.
   * @param comment The comment as text.
   */
  public addComment(element: ProjectElement, comment: ElementLog['text']): void {
    this.apiService.addElementComment(element, comment).subscribe(response => {
      this._patchElement(element, { commentCount: (element.commentCount || 0) + 1 }).subscribe(() => {
        this.toastService.open(this.translateService.get('addedcomment'), null, { horizontalPosition: 'left' });
      });
    });
  }

  /**
   * Assign user to element.
   *
   * @param element The element to assign the user to.
   * @param user The user to assign to the element.
   */
  public assignTo(element: ProjectElement, user: User | User['_id']): void {
    const userId = (typeof user === 'object'
      ? user._id
      : user) || -1;

    if (!this.userService.isInProject(userId) && userId !== -1) {
      this.toastService.open(this.translateService.get('notPossibleToAssignWhenNotInProject'));
      return;
    }
    if (element && element.assignedToUserId !== userId) {
      this._patchElement(element, { assignedToUserId: userId}).subscribe(() => {
        this.toastService.open(this.translateService.get('specificationedited'));
      });
    }
  }

  /**
   * Bulk assign to.
   *
   * @param elements List of elements.
   * @param user The user to assign to each element.
   * @param [confirm=false] Whether to show confirm modal before assigning.
   */
  public assignToBulk(elements: ProjectElement[], user: User | User['_id'], confirm: boolean = false): void {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.getPlural('specificationUpdateAllTitle'),
        content: this.translateService.getPlural('specificationUpdateAll')
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.assignToBulk(elements, user);
        }
      });
    } else {
      elements.forEach((element, i) => {
        setTimeout(() => {
          this.assignTo(element, user);
        }, i * 250);
      });
    }
  }

  /**
   * Patch the assign to options data.
   *
   * @todo Replace elementLogStore with new addComment method.
   *
   * @param element The element to patch.
   * @param settings The assign to options data.
   */
  public assignToWithOptions(element: ProjectElement, settings: ElementAssignToOptions): void {
    if (!this.userService.isInProject(settings.user) && settings.user !== -1) {
      this.toastService.open(this.translateService.get('notPossibleToAssignWhenNotInProject'));
      return;
    }
    this._patchElement(element, {
      assignedToUserId: settings.user,
      status: settings.status
    }).subscribe(() => {
      if (settings.comment) {
        element = this.getElementByCode(element.code);
        this.addComment(element, settings.comment);
      }
      this.toastService.open(this.translateService.get('specificationedited'));
    });
  }

  /**
   * Delete element.
   *
   * @note The 'restore' funcion is not really a restore. It simply acts like it. It deletes
   * the edited area template, and the area data service automatically replace it with the
   * template element. The only thing different from a regular delete is a custom dialog.
   * For a *real* 'restore' function, use the `restore` method.
   *
   * @param element The element to delete.
   * @param [confirm=false] Whether to show confirm modal before deletion.
   */
  public delete(element: ProjectElement, confirm: boolean | 'restore' = false): void {
    if (confirm) {
      const title = confirm === 'restore' ? 'restoreElementTitle' : 'deletespecificationtitle';
      const content = confirm === 'restore' ? 'restoreElementContent' : 'areyousuredeletespecification';
      const action = confirm === 'restore' ? 'restore' : 'delete';
      const actionIcon = confirm === 'restore' ? 'undo' : 'delete';

      this.modalService.confirm({
        title: this.translateService.get(title),
        content: this.translateService.get(content),
        action: this.translateService.getPlural(action),
        actionIcon: actionIcon,
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(element);
        }
      });
    } else {
      this._patchElement(element, { active: false }).subscribe(() => {
        this.toastService.open(this.translateService.get('deletedspecification'));
      });
    }
  }

  /**
   * Get element name.
   *
   * @param id The id of the element.
   * @param [match] Other properties which should match.
   * @returns Element name.
   */
  public getName(id: ProjectElement['_id'], match?: Partial<ProjectElement>): ProjectElement['name'] {
    const element = this.getElement(id, match);
    return element && element.name;
  }

  /**
   * Get the element's original value.
   *
   * Either returns the element's original value, or the value from the basic area
   * from which the element was created.
   *
   * Cases:
   *  - **Basic area**: Get original value based on template values and quality level.
   *  - **Elements**: Get value from the basic area on which the element is based.
   *
   * @param element The element to get the original value for.
   * @returns The element's original value.
   */
  public getOriginalValue(element: ProjectElement): ProjectElement['originalValue'] | ProjectElement['basicAreaValue'] {
    return element.isCustom
      ? element.basicAreaValue || element.originalValue || element.value
      : element.templateValues.find(tv => tv.quality === element.quality)
        && element.templateValues.find(tv => tv.quality === element.quality).value;
  }

  /**
   * Move element.
   *
   * @param element The element to move
   * @param from Element's previous position.
   * @param to Element's next position.
   */
  public move(element: ProjectElement, from: ElementLocation, to: ElementLocation): void {
    if (from.index === to.index && from.submodule === to.submodule) {
      return;
    }

    const fromSubmodule = this.submoduleService.getSubmodule(from.submodule);
    const toSubmodule = this.submoduleService.getSubmodule(to.submodule);

    const targetElement = fromSubmodule._id === toSubmodule._id ?
                            toSubmodule.elements[to.index > 0 ? to.index : 0] :
                            toSubmodule.elements[to.index > 0 ? to.index - 1 : 0];

    const targetLocation = {
      moduleId: toSubmodule.moduleId,
      submoduleId: toSubmodule._id,
      // Only add element info when there is a target element.
      ...(targetElement && {
        constructionId: targetElement.constructionId,
        _id: targetElement._id
      })
    };
    const position = toSubmodule.elements.filter(e => e.active).length === 0
      ? 'top'
      : to.index === 0
        ? 'above'
        : 'below';

    // Update local datastore.
    fromSubmodule.elements.splice(from.index, 1);
    toSubmodule.elements.splice(to.index, 0, element);
    this.projectService.moveElement(element, fromSubmodule, toSubmodule);

    this.apiService.moveElement(element, {
      from: from,
      to: to,
      position: position,
      target: targetLocation
    }).subscribe(() => {
      this.toastService.open(this.translateService.get('specificationEditedMoved'));
    });
  }

  /**
   * Patch the element with multiple values at once.
   *
   * @note Only use this when there's no other option available! I.e. when you need to
   *  patch multiple values which are not known in advance. Because the values can be
   *  *anything*, there's no integrity check when patching values with this method.
   *
   * @param element The element to patch.
   * @param values A (partial) set of element values to patch.
   * @param config Optional config object.
   * @returns API response.
   */
  public patch(element: ProjectElement, values: Partial<ProjectElement>, config?: any): Observable<any> {
    return new Observable(observer => {
      this._patchElement(element, values, config).subscribe(results => {
        observer.next(results);
        observer.complete();
        this.toastService.open(this.translateService.get('specificationedited'));
      });
    });
  }

  /**
   * Restore a requirement back the default state.
   *
   * @note I don't know if it's possible to *really* reset it back to default, but to
   *  make sure it's *almost* like default, the following values are reset:
   *  - assigned to user: reset to none.
   *  - status: 1, not editted.
   *  - value: The default value assigned to the reqs quality level.
   *  - valueKey: They key for the default quality level value.
   *
   * @param element The requirement to reset to default.
   * @param [confirm=false] Whether to show confirm modal.
   */
  public restore(element: ProjectElement, confirm: boolean = false): void {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('restoreElementTitle'),
        content: this.translateService.get('restoreElementContent'),
        action: this.translateService.getPlural('restore'),
        actionIcon: 'restore',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.restore(element);
        }
      });
    } else {
      this._patchElement(element, {
        assignedToUserId: -1,
        status: 1,
        value: element.templateValues.find(tv => tv.quality === element.quality)?.value,
        valueKey: element.options2[element.templateValues.findIndex(tv => tv.quality === element.quality)]?.label
      }).subscribe(() => {
        this.toastService.open(this.translateService.get('specificationrestored'));
      });
    }
  }

  /**
   * Reorder elements.
   *
   * @note To update the element's order, the parent submodule has to be patched. To keep
   *  things clean and consistent, use this method to update an element's order, the
   *  same way as updating a module or submodule order. But instead of patching the
   *  element's order property, the new order is forwarded to the SubmoduleService.
   *
   * @param submodule The element to reorder.
   * @param order The new index.
   */
  public setOrder(element: ProjectElement, order: ProjectElement['order']): void {
    const submodule = this.submoduleService.getSubmodule(element.submoduleId);
    moveItemInArray(submodule.elements, submodule.elements.findIndex(e => e._id === element._id), order);
    this.submoduleService.reorderElements(submodule.elements);
  }

  /**
   * Get the hex color code of the element's status.
   *
   * @param element The element to get the color for.
   * @returns The color code as a hex string.
   */
  public getStatusColor(element: ProjectElement): ConstantsStatusType['color'] {
    return this.constantsService.getStatusType(element.status).color;
  }

  /**
   * Set name (title).
   *
   * @param element The element to set the name (title) for.
   * @param name The new name (title).
   */
  public setName(element: ProjectElement, name: ProjectElement['name']): void {
    this._patchElement(element, { name: name }).subscribe(() => {
      this.toastService.open(this.translateService.get('specificationedited'));
    });
  }

  /**
   * Set status.
   *
   * @param element The element to set the status for.
   * @param status The new status.
   */
  public setStatus(element: ProjectElement, status: ProjectElement['status']): void {
    if (element && element.status !== status) {
      this._patchElement(element, { status: status }).subscribe(() => {
        this.toastService.open(this.translateService.get('specificationedited'));
      });
    }
  }

  /**
   * Bulk set status.
   *
   * @param elements List of elements.
   * @param user The status to assign to each element.
   * @param [confirm=false] Whether to show confirm modal before assigning.
   */
  public setStatusBulk(elements: ProjectElement[], status: ProjectElement['status'], confirm: boolean = false): void {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.getPlural('specificationUpdateAllTitle'),
        content: this.translateService.getPlural('specificationUpdateAll')
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.setStatusBulk(elements, status);
        }
      });
    } else {
      elements.forEach((element, i) => {
        setTimeout(() => {
          this.setStatus(element, status);
        }, i * 250);
      });
    }
  }

  /**
   * Set value.
   *
   * @param element The element to set the value for.
   * @param value The new value.
   * @param [config] Optional config object.
   */
  public setValue(element: ProjectElement, value: ProjectElement['value'], config?: any): void {
    const key = config && config.key || null;
    this._patchElement(element, { value: value, valueKey: key }, config).subscribe(() => {
      this.toastService.open(this.translateService.get('specificationedited'));
    });
  }
}
